import React, { useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { selectSignUpSubscriptions } from 'state/selectors/signUpSelectors';
import {
  selectOptInGroups,
  selectSelectedGroupIds,
} from 'state/selectors/subscriptionSelectors';
import * as signUpActions from 'state/actions/signUpActions';
import * as subscriptionActions from 'state/actions/subscriptionActions';
import { I18N, GlobalContext, I18NContext } from 'commons';
import IncidentSubscriptions from '../subscriptions/IncidentSubscriptions';

const SubscriptionsSection = ({
  subscriptions,
  getSubscriptions,
  getGroupSubscriptionData,
  optInGroups,
  selectedGroupIds,
  showIncidentSub
}) => {
  const {
    organization: { showAlertSubscription, showGroupSubscription },
    account: { id: contactId, CSRF_TOKEN_FOR_SESSION: csrfToken = '', name: accountName = '' },
    lang,
  } = useContext(GlobalContext);
  const i18n = useContext(I18NContext);
  let weatherCount = 0;
  const alertSubscriptionList = [];
  const getGroupSubscriptionsExpand = (currentOptInGroups, currentSelectedGroupIds) => {
    const ids = currentSelectedGroupIds.concat();
    const groupTmp = {
      groups: []
    };

    if (!currentOptInGroups || !currentSelectedGroupIds || currentSelectedGroupIds.length === 0) {
      return groupTmp;
    }

    const groupIndex = currentSelectedGroupIds.indexOf(currentOptInGroups.id);

    if (groupIndex !== -1) {
      groupTmp.name = currentOptInGroups.name;
      groupTmp.checked = true;
      if (currentOptInGroups.level) { groupTmp.level = currentOptInGroups.level; }
      ids.splice(groupIndex, 1);
    }
    if (ids.length && currentOptInGroups.groups !== null) {
      currentOptInGroups.groups.forEach(group => {
        const subGroupTmp = getGroupSubscriptionsExpand(group, ids);
        if (subGroupTmp.name) {
          groupTmp.groups.push(subGroupTmp);
        }
      });
      if (groupTmp.groups.length && !groupTmp.name) {
        groupTmp.name = currentOptInGroups.name;
        if (currentOptInGroups.level) { groupTmp.level = currentOptInGroups.level; }
      }
    }
    return groupTmp;
  };
  const handleSubscriptionsExpand = (list) => list.forEach(subscription => {
    if (subscription.type === 'Weather') {
      if (subscription.leaf) {
        weatherCount++;
      } else {
        handleSubscriptionsExpand(subscription.nodes.filter(node => node.checked));
      }
    } else if (subscription.leaf) {
      alertSubscriptionList.push({
        title: `${subscription.name}`,
        nodes: []
      });
    } else {
      subscription.nodes.forEach(node => {
        if (node.checked) {
          alertSubscriptionList.push({
            title: `${subscription.name} > ${node.name}`,
            nodes: node.nodes || []
          });
        }
      });
    }
  });
  handleSubscriptionsExpand(subscriptions.filter(subscription => subscription.checked));

  const groupSubscriptionList = useMemo(() =>
    getGroupSubscriptionsExpand(optInGroups, selectedGroupIds),
  [optInGroups, selectedGroupIds]);

  useEffect(() => {
    if (showAlertSubscription) {
      getSubscriptions(contactId);
    }
    if (showGroupSubscription) {
      getGroupSubscriptionData(contactId);
    }
  }, []);

  const treeView = (tree) => tree.map((node, index) => ((node.groups && node.groups.length > 0) ?
    <li className="group-scription-block" key={index}>
      {
        node.name &&
        <div className="group-scription-block-title">
          {
            node.checked && <i className="fas fa-check group-scription-block-icon" aria-label={i18n.get('home.subscription.checkmark.subscribed')} />
          }
          <span className={node.level ? 'group-scription-block-name' : ''} eb-test="ssp_groupSubscriptions_text_optInGroupName">{node.name}</span>
        </div>
      }
      <ul>
        {treeView(node.groups)}
      </ul>
    </li>
    :
    node.name &&
    <li key={index} className="group-scription-block-title">
      {
        node.checked && <i className="fas fa-check group-scription-block-icon" aria-label={i18n.get('home.subscription.checkmark.subscribed')} />
      }
      <span className={node.level ? 'group-scription-block-name' : ''} eb-test="ssp_groupSubscriptions_text_optInGroupName">{node.name}</span>
    </li>
  ));
  return (
    <div className="subscription-section">
      {
        showGroupSubscription && optInGroups.groups &&
        <div aria-labelledby="groupTitle">
          <div id="groupTitle" className="row subscription-title">
            {optInGroups.name}
          </div>
          <ul>{treeView(groupSubscriptionList.groups)}</ul>
        </div>
      }
      {
        showAlertSubscription &&
        <div aria-labelledby="alertTitle">
          <div id="alertTitle" className="row subscription-title">
            <I18N>ssp.main.alertSubscriptions</I18N>
          </div>
          <div>
            {
              alertSubscriptionList.map((alertSubscription, index) => (
                <div
                  key={index}
                  className="alert-subscription-block notranslate"
                >
                  <div
                    className="alert-subscription-block-title "
                    id={`alertBlockTitle${index}`}
                  >
                    {alertSubscription.title}
                  </div>
                  <ul>
                    {
                      alertSubscription.nodes.map(node => (
                        node.checked &&
                        <li key={node.serviceId} className="alert-subscription-block-list">
                          <div>
                            {node.name}
                          </div>

                        </li>
                      ))
                    }
                  </ul>

                </div>
              ))
            }
          </div>
          {
            weatherCount > 0 &&
            <div className="alert-subscription-weather-count">
              {weatherCount} Weather Alert{weatherCount > 1 ? 's' : ''}
            </div>
          }
        </div>
      }
      {showIncidentSub &&
        <IncidentSubscriptions
          contactId={contactId}
          csrfToken={csrfToken}
          accountName={accountName}
          viewName="home"
          lang={lang}
        />}
    </div>
  );
};

SubscriptionsSection.propTypes = {
  getSubscriptions: PropTypes.func.isRequired,
  getGroupSubscriptionData: PropTypes.func.isRequired,
  subscriptions: PropTypes.arrayOf(PropTypes.shape()),
  optInGroups: PropTypes.shape(),
  selectedGroupIds: PropTypes.arrayOf(PropTypes.number),
  showIncidentSub: PropTypes.bool.isRequired,
};

SubscriptionsSection.defaultProps = {
  subscriptions: [],
  optInGroups: {
    groups: []
  },
  selectedGroupIds: [],
};

const mapStateToProps = state => ({
  subscriptions: selectSignUpSubscriptions(state),
  optInGroups: selectOptInGroups(state),
  selectedGroupIds: selectSelectedGroupIds(state),
});

const mapDispatchToProps = dispatch => ({
  getSubscriptions: (accountId) => dispatch(signUpActions.getSubscriptions.action({ accountId })),
  getGroupSubscriptionData: (contactId) => dispatch(subscriptionActions.getGroupSubscriptionList.action({ contactId })),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionsSection);
